import * as Immutable from 'immutable';
import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { compose } from 'redux';

import { agreementAccept, agreementReject, resetAgreementsState, setAgreementClosed } from '../../actions/AgreementsActions';
import { getUserAgreements } from '../../actions/AuthenticationActions';
import { getUserCompetitions, hideAgreementsCompetitions } from '../../actions/CompetitionsActions';
import { ENDPOINTS_COMPETITIONS } from '../../config';
import { StateType } from '../../reducers';
import { getUserAgreementsSelector, getUserIsFetchingSelector } from '../../reducers/AuthenticationReducer/AuthenticationSelector';
import { getUserIdSelector } from '../../reducers/AuthenticationReducer/AuthenticationSelector';
import {
  competitionHideAgreementModalSelector,
  competitionUserCompetitionResultsSelector,
} from '../../reducers/CompetitionsReducer/CompetitionsSelector';
import {
  competitionAgreementTerms,
  genericAgreementTerms,
  userAgreementTerms,
  TeamAgreementTerms,
} from '../../shared/data/Agreements/AgreementTerms';
import { IQC_COMPETITION_ID } from '../../shared/constants';
import { shouldReshowIQC2026Dialog, markIQC2026DialogShown } from '../../shared/helpers/iqc2026-dialog';
import { DispatchConnect } from '../../types';

import AgreementsValidationContent from './AgreementsValidationContent';
import { getIsRejectable } from '../Agreements/AgreementsMain';

interface HOCAgreementsValidationStateProps {
  userAgreements: Immutable.List<any>;
  isFetching: boolean;
  competitions: Immutable.List<any>;
  hideAgreementModal: boolean;
  userId: string;
}

interface HOCAgreementsValidationState {
  showModal: boolean;
  agreementsProps: Object;
  competitions: any;
  fetchingCompetitions: boolean;
}

interface HOCAgreementsValidationPropsDispatchers {
  dispatchAgreementAccept: (id: string, type: 'agreements' | 'teams' | 'competitions') => Promise<any>;
  dispatchAgreementReject: (id: string, type: 'agreements' | 'teams' | 'competitions') => Promise<any>;
  hideAgreementsCompetitions: () => void;
  getUserAgreements: () => Promise<any>;
  getUserCompetitions: () => Promise<any>;
  resetAgreementsState: () => void;
  dispatchCloseAgreement: (id: string) => void;
}

export const HOCAgreementsValidation = (Component: any) =>
  class extends React.Component<any, HOCAgreementsValidationState> {
    state = {
      showModal: false,
      agreementsProps: {},
      competitions: null,
      fetchingCompetitions: false,
    };

    componentDidMount() {
      if (this.state.showModal === false && this.props.hideAgreementModal) {
        this.setState({ competitions: this.props.competitions }, () => this.setModalAndAgreementsProps());
      }
    }

    componentDidUpdate(prevProps: any, prevState: any) {
      const prevAgreementsNotAccepted = prevProps.userAgreements?.filter((agreement: any) => agreement.get('status') === 'REQUIRED');
      const currentAgreementsNotAccepted = this.props.userAgreements?.filter((agreement: any) => agreement.get('status') === 'REQUIRED');

      // Check if the number of required agreements changed while modal is open
      if (
        prevProps.userAgreements !== this.props.userAgreements &&
        this.props.userAgreements !== null &&
        this.state.showModal === true &&
        prevAgreementsNotAccepted?.size !== currentAgreementsNotAccepted?.size
      ) {
        this.setModalAndAgreementsProps();
      } else if (prevProps.userAgreements === null && this.props.userAgreements !== null && this.state.showModal === false) {
        this.setModalAndAgreementsProps();
      }

      if (
        this.state.competitions &&
        this.checkCompetitionsPending(this.state.competitions).size === 0 &&
        prevState.competitions &&
        this.checkCompetitionsPending(prevState.competitions).size !== 0
      ) {
        this.setState({ fetchingCompetitions: true }, () =>
          this.props.getUserCompetitions().then(() => {
            this.props.hideAgreementsCompetitions();
            this.setState({ fetchingCompetitions: false });
          })
        );
      }

      if (prevProps.competitions === null && this.props.competitions !== null && this.state.showModal === false) {
        this.setState({ competitions: this.props.competitions }, () => this.setModalAndAgreementsProps());
      }
    }

    setModalAndAgreementsProps() {
      const agreementsNotAccepted = this.checkAgreementsNotAccepted();
      const competitionsState: any = this.checkCompetitionsPending();

      // Get the current agreement ID before updating
      const previousAgreementId = (this.state.agreementsProps as any)?.agreementsId;

      if (agreementsNotAccepted !== undefined && agreementsNotAccepted !== null && agreementsNotAccepted.size !== 0) {
        const agreementsProps = this.setAgreementsProps(agreementsNotAccepted.first());
        const newAgreementId = (agreementsProps as any)?.agreementsId;

        // Close the previous agreement if switching to a different one
        if (previousAgreementId && previousAgreementId !== newAgreementId) {
          this.props.dispatchCloseAgreement(previousAgreementId);
        }

        if (this.state.showModal === true) {
          this.setState({ agreementsProps });
        } else {
          this.setState({ showModal: true, agreementsProps });
        }
      } else if (competitionsState && competitionsState.size !== 0) {
        const agreementsProps = this.setCompetitionProps(competitionsState.first());
        const newAgreementId = (agreementsProps as any)?.agreementsId;

        if (!newAgreementId) {
          // Competition has no valid ID — skip showing the modal
          return;
        }

        // Close the previous agreement if switching to a different one
        if (previousAgreementId && previousAgreementId !== newAgreementId) {
          this.props.dispatchCloseAgreement(previousAgreementId);
        }

        if (this.state.showModal === true) {
          this.setState({ agreementsProps });
        } else {
          this.setState({ showModal: true, agreementsProps });
        }
      } else {
        // No more agreements to show - close only the current agreement being displayed
        if (previousAgreementId) {
          this.props.dispatchCloseAgreement(previousAgreementId);
        }
        this.setState({
          showModal: false,
          agreementsProps: {},
        });
      }
    }

    checkAgreementsNotAccepted(): Immutable.Map<string, any> {
      if (this.props.userAgreements && this.props.userAgreements.size !== 0) {
        const filtered = this.props.userAgreements.filter((agreement: any) => agreement.get('status') === 'REQUIRED');
        return filtered;
      } else {
        return Immutable.Map();
      }
    }

    checkCompetitionsPending(competitionsStates: any = this.state.competitions): Immutable.List<any> {
      if (competitionsStates && competitionsStates.size !== 0) {
        const competitionPending = competitionsStates.find((competition: any) => {
          const status = competition.get('status');
          if (status === 'PENDING') return true;

          if (shouldReshowIQC2026Dialog(competition.get('id'), status, this.props.userId)) return true;
          return false;
        });

        return competitionPending ? Immutable.List([competitionPending]) : Immutable.List();
      } else {
        return Immutable.List();
      }
    }

    handleNextAgreement = (id: string) => {
      // For competitions, we need to filter and update state
      // For regular agreements, componentDidUpdate will handle showing the next one when props update
      const competitionsOldState: any = this.state.competitions;
      const competitions = competitionsOldState && competitionsOldState.filter((competition: any) => competition.get('id') !== id);
      if (competitions && competitions.size !== competitionsOldState?.size) {
        // This was a competition agreement, update state
        this.setState({ competitions }, () => this.setModalAndAgreementsProps());
      }
      // For user agreements, do nothing here - componentDidUpdate will handle it when props update
    };

    handleAgreement = (
      dispatchAgreement: any,
      id: string,
      type: 'agreements' | 'teams' | 'competitions',
      getUserData = this.props.getUserAgreements,
      successAction = 'RESPONSE_USER_AGREEMENTS_SUCCESS'
    ) => {
      dispatchAgreement(id, type).then((action: any) => {
        if (action && action.type && getUserData) {
          getUserData().then((actions: any) => {
            if (actions && actions.type && actions.type === successAction) {
              this.handleNextAgreement(id);
            }
          });
        } else if (getUserData === null) {
          this.handleNextAgreement(id);
        }
      });
    };

    handlerAcceptedAgreement = (
      id: string,
      type: 'agreements' | 'teams' | 'competitions',
      getUserData = this.props.getUserAgreements,
      successAction = 'RESPONSE_USER_AGREEMENTS_SUCCESS'
    ) => {
      this.handleAgreement(this.props.dispatchAgreementAccept, id, type, getUserData, successAction);
    };

    handlerRejectedAgreement = (
      id: string,
      type: 'agreements' | 'teams' | 'competitions',
      getUserData = this.props.getUserAgreements,
      successAction = 'RESPONSE_USER_AGREEMENTS_SUCCESS'
    ) => {
      this.handleAgreement(this.props.dispatchAgreementReject, id, type, getUserData, successAction);
    };

    getAgreementsList(type: string, id: string) {
      const agreeType = type !== 'competition' && type !== 'team' ? id : type;

      switch (agreeType) {
        case 'competition':
          return competitionAgreementTerms;
        case 'team':
          return TeamAgreementTerms;
        case 'user-agreement':
          return userAgreementTerms;
        default:
          return genericAgreementTerms;
      }
    }

    setCompetitionProps = (agreement: Immutable.Map<string, any>) => {
      const agreementId = agreement.get('id');
      if (agreement && agreementId) {
        const handleAccepted = () => this.handlerAcceptedAgreement(agreementId, 'competitions', null, 'USER_COMPETITIONS_SUCCESS');

        const handleRejected = () => {
          const isIQC = agreementId === IQC_COMPETITION_ID;

          if (isIQC && this.props.userId) {
            markIQC2026DialogShown(this.props.userId);
          }

          if (isIQC) {
            if (agreement.get('status') === 'REJECTED') {
              this.handleNextAgreement(agreementId);
            } else {
              this.handlerRejectedAgreement(agreementId, 'competitions', null, 'USER_COMPETITIONS_SUCCESS');
            }
          } else {
            this.handleNextAgreement(agreementId);
          }
        };

        const handleClose = () => this.handleNextAgreement(agreementId);

        return Object.assign(
          {},
          {
            modifiers: ['competition'],
            shouldShowAgreeButton: false,
            agreementsId: agreementId,
            agreementType: 'competition',
            banner: `${ENDPOINTS_COMPETITIONS.competitions}/${agreementId}/banner`,
            handleAccepted,
            handleRejected,
            isEnrollModal: true,
            modalClass: 'competition-guideline-modal',
            modalCloseIcon: true,
            competitionName: agreement.get('name'),
            competitionId: agreementId,
            handleClose,
          }
        );
      } else {
        return {};
      }
    };

    setAgreementsProps(agreement: Immutable.Map<string, any>) {
      const validKeys = Immutable.List(['agreement', 'competition', 'team']);
      let agreementsProps = {};
      const endpoints: any = {
        agreement: 'agreements',
        team: 'teams',
        competition: 'competitions',
      };

      agreement.forEach((value: any, key: any) => {
        if (validKeys.includes(key)) {
          const id = value.get('id');
          const modifiers = key === 'competition' ? ['competition'] : id === 'user-agreement' ? ['user-agreement'] : [];

          const type: 'agreements' | 'teams' | 'competitions' = endpoints[key];

          const handleAccepted = () => this.handlerAcceptedAgreement(id, type);

          const isRejectable = getIsRejectable(value);
          const handleRejected = isRejectable ? () => this.handlerRejectedAgreement(id, type) : undefined;

          const agreementsList = this.getAgreementsList(key, id);

          agreementsProps = Object.assign({}, agreementsProps, {
            modifiers,
            shouldShowAgreeButton: key === 'agreement' && id !== 'user-agreement' ? true : false,
            agreementsId: id,
            agreementType: key,
            agreementsList,
            banner: key === 'competition' ? `${ENDPOINTS_COMPETITIONS.competitions}/${id}/banner` : undefined,
            handleAccepted: handleAccepted,
            handleRejected: handleRejected,
          });
        }
      });

      return agreementsProps;
    }

    render() {
      if (this.props.needsRecertification) {
        return <Component {...this.props} />;
      } else {
        return (
          <React.Fragment>
            {this.state.showModal && (
              <AgreementsValidationContent
                agreementsProps={this.state.agreementsProps}
                isFetching={this.props.userAgreements && (this.props.isFetching === true || this.state.fetchingCompetitions)}
              />
            )}
            <Component {...this.props} />
          </React.Fragment>
        );
      }
    }
  };

const mapStateToProps = (state: StateType): HOCAgreementsValidationStateProps => ({
  // @ts-ignore
  userAgreements: getUserAgreementsSelector(state),
  // @ts-ignore
  isFetching: getUserIsFetchingSelector(state),
  // @ts-ignore
  competitions: competitionUserCompetitionResultsSelector(state),
  // @ts-ignore
  hideAgreementModal: competitionHideAgreementModalSelector(state),
  // @ts-ignore
  userId: getUserIdSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<any>): HOCAgreementsValidationPropsDispatchers => ({
  dispatchAgreementAccept: async (id: string, type: 'agreements' | 'teams' | 'competitions') => dispatch(agreementAccept(id, type)),
  dispatchAgreementReject: async (id: string, type: 'agreements' | 'teams' | 'competitions') => dispatch(agreementReject(id, type)),
  getUserAgreements: async () => dispatch(getUserAgreements()),
  getUserCompetitions: async () => dispatch(getUserCompetitions('self', false)),
  hideAgreementsCompetitions: async () => dispatch(hideAgreementsCompetitions()),
  resetAgreementsState: () => dispatch(resetAgreementsState()),
  dispatchCloseAgreement: (id: string) => dispatch(setAgreementClosed({ id })),
});

export default compose<any>(
  connect<HOCAgreementsValidationStateProps, DispatchConnect<HOCAgreementsValidationPropsDispatchers>, any, StateType>(
    mapStateToProps,
    mapDispatchToProps
  ),
  HOCAgreementsValidation
);
